import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Popover,
  IconButton
} from '@mui/material';
import { supabase } from '../../lib/supabase';
import { fetchWorkers } from '../../services/workerService';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PaymentsIcon from '@mui/icons-material/Payments';

interface PayrollTabProps {
  farmId: string;
}

interface PayrollRecord {
  id: string;
  worker_id: string;
  pay_period_start: string;
  pay_period_end: string;
  base_salary: number;
  overtime_hours?: number;
  overtime_rate?: number;
  bonuses?: number;
  deductions?: number;
  total_pay: number;
  worker_name?: string;
  period_start?: string;
  period_end?: string;
  base_pay?: number;
  advances_taken?: number;
  net_pay?: number;
  status?: string;
  paid?: boolean;
  paid_at?: string;
  notes?: string;
}

interface Worker {
  id: string;
  name: string;
  hourly_rate?: number;
}

const PayrollTab: React.FC<PayrollTabProps> = ({ farmId }) => {
  const [payrollRecords, setPayrollRecords] = useState<PayrollRecord[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<PayrollRecord | null>(null);
  const [editForm, setEditForm] = useState<any>(null);
  const [popoverAnchor, setPopoverAnchor] = useState<null | HTMLElement>(null);
  const [popoverRecord, setPopoverRecord] = useState<PayrollRecord | null>(null);
  const [formData, setFormData] = useState({
    worker_id: '',
    pay_period_start: '',
    pay_period_end: '',
    base_salary: 0,
    overtime_hours: 0,
    overtime_rate: 0,
    bonuses: 0,
    deductions: 0,
    advances_taken: 0,
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [farmId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch workers for this farm
      const workersData = await fetchWorkers(farmId);
      setWorkers(workersData);

      // Fetch payroll records for this farm
      const { data: payrollData, error: payrollError } = await supabase
        .from('payroll_records')
        .select(`*, workers!inner(name, farm_id)`)
        .eq('workers.farm_id', farmId)
        .order('period_end', { ascending: false });

      if (payrollError) throw payrollError;

      const formattedRecords = (payrollData || []).map((record: any) => ({
        ...record,
        worker_name: record.workers?.name || 'Unknown',
      }));

      setPayrollRecords(formattedRecords);
    } catch (err) {
      console.error('Error loading payroll data:', err);
      setError('Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalPay = () => {
    const overtimePay = formData.overtime_hours * formData.overtime_rate;
    return formData.base_salary + overtimePay + formData.bonuses - formData.deductions;
  };

  const handleAddPayroll = async () => {
    try {
      setError(null);
      
      const totalPay = calculateTotalPay();
      
      const { error } = await supabase
        .from('payroll_records')
        .insert([
          {
            worker_id: formData.worker_id,
            farm_id: farmId,
            period_start: formData.pay_period_start,
            period_end: formData.pay_period_end,
            period: `${formData.pay_period_start} - ${formData.pay_period_end}`,
            base_pay: formData.base_salary,
            advances_taken: formData.advances_taken || 0,
            deductions: formData.deductions || 0,
            bonuses: formData.bonuses || 0,
            net_pay: totalPay,
            amount: totalPay,
            status: 'pending',
            paid: false,
            paid_at: null,
            notes: formData.notes || '',
          },
        ]);

      if (error) throw error;

      setDialogOpen(false);
      setFormData({
        worker_id: '',
        pay_period_start: '',
        pay_period_end: '',
        base_salary: 0,
        overtime_hours: 0,
        overtime_rate: 0,
        bonuses: 0,
        deductions: 0,
        advances_taken: 0,
        notes: '',
      });
      loadData();
    } catch (err) {
      console.error('Error adding payroll record:', err);
      setError('Failed to add payroll record');
    }
  };

  // CRUD: Edit
  const handleEditClick = (record: PayrollRecord) => {
    setEditForm({ ...record });
    setSelectedRecord(record);
    setEditDialogOpen(true);
  };

  const handleEditFormChange = (field: string, value: any) => {
    setEditForm((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleEditPayrollSubmit = async () => {
    try {
      if (!selectedRecord) return;
      const totalPay =
        (Number(editForm.base_pay) || 0) +
        ((Number(editForm.overtime_hours) || 0) * (Number(editForm.overtime_rate) || 0)) +
        (Number(editForm.bonuses) || 0) -
        (Number(editForm.deductions) || 0);
      const { error } = await supabase
        .from('payroll_records')
        .update({
          worker_id: editForm.worker_id,
          period_start: editForm.period_start || editForm.pay_period_start,
          period_end: editForm.period_end || editForm.pay_period_end,
          period: `${editForm.period_start || editForm.pay_period_start} - ${editForm.period_end || editForm.pay_period_end}`,
          base_pay: editForm.base_pay,
          advances_taken: editForm.advances_taken || 0,
          deductions: editForm.deductions || 0,
          bonuses: editForm.bonuses || 0,
          net_pay: totalPay,
          amount: totalPay,
          status: editForm.status || 'pending',
          paid: editForm.paid || false,
          paid_at: editForm.paid_at || null,
          notes: editForm.notes || '',
        })
        .eq('id', selectedRecord.id)
        .eq('farm_id', farmId);
      if (error) throw error;
      setEditDialogOpen(false);
      setSelectedRecord(null);
      setEditForm(null);
      loadData();
    } catch (err) {
      setError('Failed to update payroll record');
    }
  };

  // CRUD: Delete
  const handleDeleteClick = (record: PayrollRecord) => {
    setSelectedRecord(record);
    setDeleteDialogOpen(true);
  };

  const handleDeletePayroll = async () => {
    try {
      if (!selectedRecord) return;
      const { error } = await supabase
        .from('payroll_records')
        .delete()
        .eq('id', selectedRecord.id)
        .eq('farm_id', farmId);
      if (error) throw error;
      setDeleteDialogOpen(false);
      setSelectedRecord(null);
      loadData();
    } catch (err) {
      setError('Failed to delete payroll record');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Payroll Records</Typography>
        <Button variant="contained" onClick={() => setDialogOpen(true)}>
          Add Payroll
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Worker</TableCell>
              <TableCell>Pay Period</TableCell>
              <TableCell>Base Salary</TableCell>
              <TableCell>Overtime</TableCell>
              <TableCell>Bonuses</TableCell>
              <TableCell>Deductions</TableCell>
              <TableCell>Total Pay</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payrollRecords.map((record) => (
              <TableRow
                key={record.id}
                hover
                style={{ cursor: 'pointer' }}
                onClick={e => {
                  setPopoverAnchor(e.currentTarget);
                  setPopoverRecord(record);
                }}
              >
                <TableCell>{record.worker_name}</TableCell>
                <TableCell>
                  {record.period_start ? new Date(record.period_start).toLocaleDateString() : '-'} -
                  {record.period_end ? new Date(record.period_end).toLocaleDateString() : '-'}
                </TableCell>
                <TableCell>${record.base_pay?.toLocaleString() || 0}</TableCell>
                <TableCell>
                  {record.overtime_hours || 0}h @ ${record.overtime_rate || 0}/h
                </TableCell>
                <TableCell>${record.bonuses?.toLocaleString() || 0}</TableCell>
                <TableCell>${record.deductions?.toLocaleString() || 0}</TableCell>
                <TableCell><strong>${record.net_pay?.toLocaleString() || 0}</strong></TableCell>
                <TableCell>
                  <IconButton onClick={e => { e.stopPropagation(); handleEditClick(record); }} size="small"><EditIcon /></IconButton>
                  <IconButton onClick={e => { e.stopPropagation(); handleDeleteClick(record); }} size="small"><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={() => { setPopoverAnchor(null); setPopoverRecord(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 350, maxWidth: 500 }}>
          {popoverRecord && (
            <>
              <Typography variant="h6" gutterBottom>{popoverRecord.worker_name}</Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom><b>Pay Period:</b> {popoverRecord.period_start ? new Date(popoverRecord.period_start).toLocaleDateString() : '-'} - {popoverRecord.period_end ? new Date(popoverRecord.period_end).toLocaleDateString() : '-'}</Typography>
              <Typography variant="body2"><b>Base Pay:</b> ${popoverRecord.base_pay?.toLocaleString() || 0}</Typography>
              <Typography variant="body2"><b>Advances Taken:</b> ${popoverRecord.advances_taken?.toLocaleString() || 0}</Typography>
              <Typography variant="body2"><b>Bonuses:</b> ${popoverRecord.bonuses?.toLocaleString() || 0}</Typography>
              <Typography variant="body2"><b>Deductions:</b> ${popoverRecord.deductions?.toLocaleString() || 0}</Typography>
              <Typography variant="body2"><b>Net Pay:</b> ${popoverRecord.net_pay?.toLocaleString() || 0}</Typography>
              <Typography variant="body2"><b>Status:</b> {popoverRecord.status || '-'}</Typography>
              <Typography variant="body2"><b>Paid:</b> {popoverRecord.paid ? 'Yes' : 'No'}</Typography>
              <Typography variant="body2"><b>Paid At:</b> {popoverRecord.paid_at ? new Date(popoverRecord.paid_at).toLocaleString() : '-'}</Typography>
              <Typography variant="body2"><b>Notes:</b> {popoverRecord.notes || '-'}</Typography>
            </>
          )}
        </Box>
      </Popover>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Payroll Record</DialogTitle>
        <DialogContent>
          {editForm && (
            <Box sx={{ pt: 1 }}>
              <TextField
                label="Pay Period Start"
                type="date"
                fullWidth
                value={editForm.period_start || editForm.pay_period_start}
                onChange={e => handleEditFormChange('period_start', e.target.value)}
                margin="normal"
                required
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Pay Period End"
                type="date"
                fullWidth
                value={editForm.period_end || editForm.pay_period_end}
                onChange={e => handleEditFormChange('period_end', e.target.value)}
                margin="normal"
                required
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Base Pay"
                type="number"
                fullWidth
                value={editForm.base_pay}
                onChange={e => handleEditFormChange('base_pay', Number(e.target.value))}
                margin="normal"
                required
              />
              <TextField
                label="Advances Taken"
                type="number"
                fullWidth
                value={editForm.advances_taken || 0}
                onChange={e => handleEditFormChange('advances_taken', Number(e.target.value))}
                margin="normal"
              />
              <TextField
                label="Bonuses"
                type="number"
                fullWidth
                value={editForm.bonuses || 0}
                onChange={e => handleEditFormChange('bonuses', Number(e.target.value))}
                margin="normal"
              />
              <TextField
                label="Deductions"
                type="number"
                fullWidth
                value={editForm.deductions || 0}
                onChange={e => handleEditFormChange('deductions', Number(e.target.value))}
                margin="normal"
              />
              <TextField
                label="Status"
                select
                fullWidth
                value={editForm.status || 'pending'}
                onChange={e => handleEditFormChange('status', e.target.value)}
                margin="normal"
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
              </TextField>
              <TextField
                label="Notes"
                fullWidth
                multiline
                rows={2}
                value={editForm.notes || ''}
                onChange={e => handleEditFormChange('notes', e.target.value)}
                margin="normal"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditPayrollSubmit} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Payroll Record</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this payroll record?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeletePayroll}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Payroll Record</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              select
              label="Worker"
              fullWidth
              value={formData.worker_id}
              onChange={(e) => setFormData({ ...formData, worker_id: e.target.value })}
              margin="normal"
              required
            >
              {workers.map((worker) => (
                <MenuItem key={worker.id} value={worker.id}>
                  {worker.name}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Pay Period Start"
                type="date"
                value={formData.pay_period_start}
                onChange={(e) => setFormData({ ...formData, pay_period_start: e.target.value })}
                margin="normal"
                required
                sx={{ flex: 1 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Pay Period End"
                type="date"
                value={formData.pay_period_end}
                onChange={(e) => setFormData({ ...formData, pay_period_end: e.target.value })}
                margin="normal"
                required
                sx={{ flex: 1 }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <TextField
              label="Base Salary"
              type="number"
              fullWidth
              value={formData.base_salary}
              onChange={(e) => setFormData({ ...formData, base_salary: Number(e.target.value) })}
              margin="normal"
              required
              inputProps={{ min: 0, step: 0.01 }}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Overtime Hours"
                type="number"
                value={formData.overtime_hours}
                onChange={(e) => setFormData({ ...formData, overtime_hours: Number(e.target.value) })}
                margin="normal"
                sx={{ flex: 1 }}
                inputProps={{ min: 0, step: 0.5 }}
              />
              <TextField
                label="Overtime Rate"
                type="number"
                value={formData.overtime_rate}
                onChange={(e) => setFormData({ ...formData, overtime_rate: Number(e.target.value) })}
                margin="normal"
                sx={{ flex: 1 }}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Bonuses"
                type="number"
                value={formData.bonuses}
                onChange={(e) => setFormData({ ...formData, bonuses: Number(e.target.value) })}
                margin="normal"
                sx={{ flex: 1 }}
                inputProps={{ min: 0, step: 0.01 }}
              />
              <TextField
                label="Deductions"
                type="number"
                value={formData.deductions}
                onChange={(e) => setFormData({ ...formData, deductions: Number(e.target.value) })}
                margin="normal"
                sx={{ flex: 1 }}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Box>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="h6">
                Total Pay: ${calculateTotalPay().toLocaleString()}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddPayroll} variant="contained">
            Add Record
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayrollTab;